import { useState, useEffect, useRef, useCallback } from 'react';
import {
  Star, ThumbsUp, X, ChevronLeft, ChevronRight,
  Camera, MessageSquare, CheckCircle2, Loader2,
} from 'lucide-react';

const apiBase = () => (window.themeData?.apiUrl ?? '').replace(/\/$/, '') + '/little-anvi/v1';
const nonce   = () => window.themeData?.nonce ?? '';

// ── Interactive star picker ──────────────────────────────────────────────────
function StarPicker({ value, onChange }) {
  const [hovered, setHovered] = useState(0);
  const display = hovered || value;
  const labels  = ['', 'Poor', 'Fair', 'Good', 'Very Good', 'Excellent'];
  return (
    <div className="flex items-center gap-3">
      <div className="flex gap-1">
        {[1, 2, 3, 4, 5].map(n => (
          <button
            key={n} type="button"
            onMouseEnter={() => setHovered(n)}
            onMouseLeave={() => setHovered(0)}
            onClick={() => onChange(n)}
            className="focus:outline-none transition-transform hover:scale-110"
          >
            <Star className={`w-9 h-9 transition-colors ${n <= display ? 'text-yellow-400 fill-current' : 'text-gray-300'}`} />
          </button>
        ))}
      </div>
      {display > 0 && (
        <span className="text-sm font-bold text-yellow-500">{labels[display]}</span>
      )}
    </div>
  );
}

// ── Read-only star row ────────────────────────────────────────────────────────
function StarDisplay({ rating, size = 'sm' }) {
  const cls = size === 'sm' ? 'w-4 h-4' : 'w-5 h-5';
  return (
    <div className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map(n => (
        <Star key={n} className={`${cls} ${n <= Math.round(rating) ? 'text-yellow-400 fill-current' : 'text-gray-200'}`} />
      ))}
    </div>
  );
}

// ── Image lightbox modal ──────────────────────────────────────────────────────
function ImageLightbox({ images, startIdx, onClose }) {
  const [idx, setIdx] = useState(startIdx);

  useEffect(() => {
    const handler = e => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 bg-black/85 z-[200] flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div className="relative max-w-3xl w-full" onClick={e => e.stopPropagation()}>
        <button
          onClick={onClose}
          className="absolute -top-10 right-0 text-white/80 hover:text-white transition-colors"
        >
          <X className="w-7 h-7" />
        </button>

        <img
          src={images[idx].url}
          alt=""
          className="w-full max-h-[75vh] object-contain rounded-2xl"
        />

        {images.length > 1 && (
          <>
            <button
              onClick={() => setIdx(i => Math.max(0, i - 1))}
              disabled={idx === 0}
              className="absolute left-3 top-1/2 -translate-y-1/2 w-11 h-11 bg-white/20 hover:bg-white/40 rounded-full flex items-center justify-center text-white disabled:opacity-25 transition-all"
            >
              <ChevronLeft className="w-6 h-6" />
            </button>
            <button
              onClick={() => setIdx(i => Math.min(images.length - 1, i + 1))}
              disabled={idx === images.length - 1}
              className="absolute right-3 top-1/2 -translate-y-1/2 w-11 h-11 bg-white/20 hover:bg-white/40 rounded-full flex items-center justify-center text-white disabled:opacity-25 transition-all"
            >
              <ChevronRight className="w-6 h-6" />
            </button>
          </>
        )}

        {/* Thumbnail strip */}
        <div className="flex justify-center gap-2 mt-4 flex-wrap">
          {images.map((img, i) => (
            <button
              key={i} onClick={() => setIdx(i)}
              className={`w-16 h-16 rounded-xl overflow-hidden border-2 transition-all ${
                i === idx ? 'border-white scale-105' : 'border-transparent opacity-55 hover:opacity-90'
              }`}
            >
              <img src={img.thumb || img.url} alt="" className="w-full h-full object-cover" />
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

// ── Horizontal image thumbnail row ────────────────────────────────────────────
function ReviewImages({ images }) {
  const [lightboxIdx, setLightboxIdx] = useState(null);
  if (!images?.length) return null;
  return (
    <>
      <div className="flex gap-2 mt-3 overflow-x-auto pb-1 scrollbar-hide">
        {images.map((img, i) => (
          <button
            key={i} onClick={() => setLightboxIdx(i)}
            className="w-[72px] h-[72px] flex-shrink-0 rounded-xl overflow-hidden border-2 border-gray-100 hover:border-primary transition-colors"
          >
            <img src={img.thumb || img.url} alt="" className="w-full h-full object-cover" />
          </button>
        ))}
      </div>
      {lightboxIdx !== null && (
        <ImageLightbox images={images} startIdx={lightboxIdx} onClose={() => setLightboxIdx(null)} />
      )}
    </>
  );
}

// ── Single review card ────────────────────────────────────────────────────────
function ReviewCard({ review }) {
  const [likes,  setLikes]  = useState(review.likes_count);
  const [liked,  setLiked]  = useState(review.liked_by_me);
  const [liking, setLiking] = useState(false);
  const isLoggedIn = !!window.themeData?.isLoggedIn;

  async function handleLike() {
    if (!isLoggedIn || liking) return;
    setLiking(true);
    try {
      const r = await fetch(`${apiBase()}/reviews/${review.id}/like`, {
        method: 'POST',
        headers: { 'X-WP-Nonce': nonce() },
        credentials: 'same-origin',
      });
      if (r.ok) {
        const d = await r.json();
        setLikes(d.count);
        setLiked(d.liked);
      }
    } catch (_) {}
    setLiking(false);
  }

  const date = new Date(review.date).toLocaleDateString('en-IN', {
    day: 'numeric', month: 'short', year: 'numeric',
  });

  return (
    <div className="border-b border-gray-100 py-6 last:border-0">
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center text-primary font-black text-sm flex-shrink-0">
            {review.author.charAt(0).toUpperCase()}
          </div>
          <div>
            <p className="font-bold text-sm text-textDark leading-tight">{review.author}</p>
            <p className="text-xs text-gray-400 mt-0.5">{date}</p>
          </div>
        </div>
        {review.verified && (
          <span className="flex items-center gap-1 text-xs font-bold text-accent3 bg-accent3/10 px-2.5 py-1 rounded-lg flex-shrink-0">
            <CheckCircle2 className="w-3 h-3" />
            Verified Purchase
          </span>
        )}
      </div>

      <StarDisplay rating={review.rating} />

      {review.content && (
        <p className="text-sm text-gray-600 mt-2.5 leading-relaxed">{review.content}</p>
      )}

      <ReviewImages images={review.images || []} />

      <button
        onClick={handleLike}
        disabled={!isLoggedIn || liking}
        className={`flex items-center gap-1.5 text-xs font-semibold px-3.5 py-2 rounded-xl border transition-all mt-4
          ${liked
            ? 'border-primary bg-primary/5 text-primary'
            : 'border-gray-200 text-gray-500 hover:border-gray-300 hover:text-gray-700'}
          ${!isLoggedIn ? 'cursor-default opacity-50' : 'cursor-pointer'}
        `}
        title={!isLoggedIn ? 'Log in to mark as helpful' : ''}
      >
        <ThumbsUp className={`w-3.5 h-3.5 ${liked ? 'fill-current' : ''}`} />
        Helpful{likes > 0 ? ` (${likes})` : ''}
      </button>
    </div>
  );
}

// ── Review submission form ────────────────────────────────────────────────────
function ReviewForm({ productId, onSubmitted }) {
  const [rating,     setRating]     = useState(0);
  const [content,    setContent]    = useState('');
  const [images,     setImages]     = useState([]);
  const [uploading,  setUploading]  = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error,      setError]      = useState('');
  const fileRef = useRef();

  async function uploadFile(file) {
    const fd = new FormData();
    fd.append('image', file);
    const r = await fetch(`${apiBase()}/reviews/upload-image`, {
      method: 'POST',
      headers: { 'X-WP-Nonce': nonce() },
      credentials: 'same-origin',
      body: fd,
    });
    if (!r.ok) throw new Error('Upload failed');
    return r.json();
  }

  async function handleFiles(files) {
    if (!files.length) return;
    setUploading(true);
    setError('');
    const toUpload = Array.from(files).filter(f => f.type.startsWith('image/')).slice(0, 6 - images.length);
    for (const file of toUpload) {
      const preview = URL.createObjectURL(file);
      setImages(prev => [...prev, { preview, uploading: true }]);
      try {
        const data = await uploadFile(file);
        setImages(prev => prev.map(img => img.preview === preview ? { ...data, preview } : img));
      } catch (_) {
        setImages(prev => prev.filter(img => img.preview !== preview));
        setError('One or more images failed to upload. Please try again.');
      }
    }
    setUploading(false);
  }

  function removeImage(idx) {
    setImages(prev => prev.filter((_, i) => i !== idx));
  }

  async function handleSubmit(e) {
    e.preventDefault();
    if (!rating)        { setError('Please select a star rating.'); return; }
    if (!content.trim()) { setError('Please write your review.'); return; }
    setSubmitting(true);
    setError('');
    try {
      const imageIds = images.filter(i => i.id).map(i => i.id);
      const r = await fetch(`${apiBase()}/products/${productId}/reviews`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-WP-Nonce': nonce() },
        credentials: 'same-origin',
        body: JSON.stringify({ rating, content, images: imageIds }),
      });
      const d = await r.json();
      if (!r.ok) { setError(d.error || 'Failed to submit review.'); setSubmitting(false); return; }
      onSubmitted();
    } catch (_) {
      setError('Network error. Please try again.');
      setSubmitting(false);
    }
  }

  return (
    <form onSubmit={handleSubmit} className="bg-gray-50 rounded-2xl p-6 mb-8">
      <h3 className="font-black text-textDark text-lg mb-6 flex items-center gap-2">
        <MessageSquare className="w-5 h-5 text-primary" />
        Write a Review
      </h3>

      {/* Stars */}
      <div className="mb-5">
        <label className="text-sm font-bold text-gray-700 block mb-2">
          Your Rating <span className="text-red-400">*</span>
        </label>
        <StarPicker value={rating} onChange={setRating} />
      </div>

      {/* Comment */}
      <div className="mb-5">
        <label className="text-sm font-bold text-gray-700 block mb-2">
          Your Review <span className="text-red-400">*</span>
        </label>
        <textarea
          value={content}
          onChange={e => setContent(e.target.value)}
          placeholder="Share your experience with this product..."
          rows={4}
          className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm text-gray-700 resize-none focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/10 bg-white transition-colors"
        />
      </div>

      {/* Photo upload */}
      <div className="mb-6">
        <label className="text-sm font-bold text-gray-700 block mb-2">
          Add Photos{' '}
          <span className="text-xs text-gray-400 font-normal">(optional · max 6)</span>
        </label>
        <div className="flex gap-2 flex-wrap">
          {images.map((img, i) => (
            <div key={i} className="relative w-20 h-20 rounded-xl overflow-hidden border border-gray-200 bg-gray-100 flex-shrink-0">
              <img src={img.preview || img.url} alt="" className={`w-full h-full object-cover ${img.uploading ? 'opacity-40' : ''}`} />
              {img.uploading && (
                <div className="absolute inset-0 flex items-center justify-center">
                  <Loader2 className="w-5 h-5 text-primary animate-spin" />
                </div>
              )}
              {!img.uploading && (
                <button
                  type="button" onClick={() => removeImage(i)}
                  className="absolute top-1 right-1 w-5 h-5 bg-black/65 rounded-full flex items-center justify-center text-white hover:bg-black"
                >
                  <X className="w-3 h-3" />
                </button>
              )}
            </div>
          ))}
          {images.length < 6 && (
            <button
              type="button" onClick={() => fileRef.current?.click()}
              className="w-20 h-20 rounded-xl border-2 border-dashed border-gray-300 flex flex-col items-center justify-center gap-1 text-gray-400 hover:border-primary hover:text-primary transition-colors flex-shrink-0"
            >
              <Camera className="w-5 h-5" />
              <span className="text-[11px] font-bold">Add</span>
            </button>
          )}
        </div>
        <input
          ref={fileRef} type="file" accept="image/*" multiple className="hidden"
          onChange={e => handleFiles(e.target.files)}
        />
      </div>

      {error && (
        <p className="text-sm text-red-500 bg-red-50 border border-red-100 px-4 py-2.5 rounded-xl mb-4">
          {error}
        </p>
      )}

      <button
        type="submit"
        disabled={submitting || uploading}
        className="bg-primary hover:bg-primaryHover text-white font-black px-8 py-3 rounded-xl transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
      >
        {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
        {submitting ? 'Submitting…' : 'Submit Review'}
      </button>
    </form>
  );
}

// ── Rating summary bar chart ──────────────────────────────────────────────────
function RatingSummary({ reviews }) {
  const avg = reviews.length
    ? (reviews.reduce((s, r) => s + r.rating, 0) / reviews.length).toFixed(1)
    : 0;
  return (
    <div className="flex items-center gap-8 mb-8 p-6 bg-gray-50 rounded-2xl">
      <div className="text-center flex-shrink-0">
        <div className="text-5xl font-black text-textDark leading-none">{avg}</div>
        <div className="mt-1.5">
          <StarDisplay rating={parseFloat(avg)} size="sm" />
        </div>
        <p className="text-xs text-gray-400 mt-1.5">
          {reviews.length} review{reviews.length !== 1 ? 's' : ''}
        </p>
      </div>
      <div className="flex-1 space-y-1.5">
        {[5, 4, 3, 2, 1].map(star => {
          const count = reviews.filter(r => r.rating === star).length;
          const pct   = reviews.length ? (count / reviews.length) * 100 : 0;
          return (
            <div key={star} className="flex items-center gap-2">
              <span className="text-xs text-gray-500 w-2 text-right">{star}</span>
              <Star className="w-3 h-3 text-yellow-400 fill-current flex-shrink-0" />
              <div className="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden">
                <div
                  className="h-full bg-yellow-400 rounded-full transition-all duration-500"
                  style={{ width: `${pct}%` }}
                />
              </div>
              <span className="text-xs text-gray-400 w-4 text-right">{count}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── Main ReviewSection export ─────────────────────────────────────────────────
export default function ReviewSection({ productId }) {
  const [reviews,   setReviews]   = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [canReview, setCanReview] = useState(null);
  const [reason,    setReason]    = useState('');
  const [submitted, setSubmitted] = useState(false);
  const isLoggedIn = !!window.themeData?.isLoggedIn;

  const loadReviews = useCallback(() => {
    fetch(`${apiBase()}/products/${productId}/reviews`)
      .then(r => r.json())
      .then(d => { setReviews(Array.isArray(d) ? d : []); setLoading(false); })
      .catch(() => setLoading(false));
  }, [productId]);

  useEffect(() => {
    loadReviews();
    if (!isLoggedIn) {
      setCanReview(false);
      setReason('not_logged_in');
      return;
    }
    fetch(`${apiBase()}/products/${productId}/can-review`, {
      headers: { 'X-WP-Nonce': nonce() },
      credentials: 'same-origin',
    })
      .then(r => r.json())
      .then(d => { setCanReview(d.can_review); setReason(d.reason ?? ''); })
      .catch(() => setCanReview(false));
  }, [productId, isLoggedIn, loadReviews]);

  function handleSubmitted() {
    setSubmitted(true);
    setCanReview(false);
    setReason('already_reviewed');
    loadReviews();
  }

  const myAccountUrl = window.themeData?.siteUrl
    ? `${window.themeData.siteUrl}/my-account/`
    : '/my-account/';

  return (
    <div>
      {reviews.length > 0 && <RatingSummary reviews={reviews} />}

      {/* Success banner */}
      {submitted && (
        <div className="flex items-center gap-3 bg-accent3/10 text-accent3 font-bold px-5 py-4 rounded-2xl mb-6">
          <CheckCircle2 className="w-5 h-5 flex-shrink-0" />
          Thank you! Your review has been submitted.
        </div>
      )}

      {/* Review form or gating message */}
      {canReview && !submitted && (
        <ReviewForm productId={productId} onSubmitted={handleSubmitted} />
      )}
      {!canReview && canReview !== null && !submitted && (
        <div className="text-sm text-gray-500 bg-gray-50 border border-gray-100 px-5 py-4 rounded-2xl mb-6">
          {reason === 'not_logged_in' && (
            <><a href={myAccountUrl} className="text-primary font-bold hover:underline">Log in</a> to leave a review.</>
          )}
          {reason === 'not_purchased' && (
            <>Only customers who have purchased this product can leave a review.</>
          )}
          {reason === 'already_reviewed' && (
            <>You have already reviewed this product.</>
          )}
        </div>
      )}

      {/* Reviews list */}
      {loading && (
        <div className="flex justify-center py-10 text-primary">
          <Loader2 className="w-6 h-6 animate-spin" />
        </div>
      )}
      {!loading && reviews.length > 0 && (
        <div>
          {reviews.map(review => (
            <ReviewCard key={review.id} review={review} />
          ))}
        </div>
      )}
      {!loading && reviews.length === 0 && !submitted && (
        <p className="text-center text-gray-400 text-sm py-10">
          No reviews yet. Be the first to share your experience!
        </p>
      )}
    </div>
  );
}
